import BulkEndorsementUpload from "./BulkEndorsementUpload";
import TemplateEditor, { overrideLogMessage } from "./TemplateEditor";
import * as support from "./Templates.support";
import {
  AddRounded as AddIcon,
  DeleteRounded as DeleteIcon,
  EditRounded as EditIcon,
  SaveAltRounded as DownloadIcon,
} from "@mui/icons-material";
import {
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { makeNewId } from "common/ids";
import Component from "components/Component";
import RawConfigEditor from "components/admin/RawConfigEditor";
import Button from "components/common/Button";
import DefaultLoader from "components/common/DefaultLoader";
import IconButton from "components/common/IconButton";
import TemplateValidationDialog from "components/common/TemplateValidationDialog";
import FileDetail from "components/templates/FileDetail";
import * as config from "config";
import * as fileUtils from "fileUtils";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  toStructuredError,
  useLazyGetStaticDataQuery,
  useUpdateStaticDataMutation,
} from "services/adminService";
import * as utils from "utils";

const countErrors = (reports) =>
  (reports ?? []).reduce(
    (total, report) => total + (report.errors ?? []).length,
    0
  );

const DisplayTemplate = ({
  template,
  onEdit,
  onDelete,
  companyId,
  templates,
}) => {
  const [downloading, setDownloading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [reason, setReason] = useState("");

  const download = async () => {
    setDownloading(true);
    await fileUtils.saveFile(template.fileId, template.filename, companyId);
    setDownloading(false);
  };

  const handleDeleteClick = () => {
    setConfirmDelete(true);
  };

  const handleConfirmDelete = () => {
    onDelete({ template, userLogMessage: reason });
    setConfirmDelete(false);
  };

  const handleCancelDelete = () => {
    setConfirmDelete(false);
  };

  const variablesString = Object.keys(template.variables ?? []).join(", ");

  const reference =
    template.templateType === "REFERENCE"
      ? (templates ?? []).filter(
          ({ id }) => id === template.referenceTemplateId
        )?.[0]?.title ?? "[Invalid]"
      : null;

  return (
    <>
      <Dialog
        open={confirmDelete}
        onClose={handleCancelDelete}
        aria-labelledby={"alert-dialog-title"}
        aria-describedby={"alert-dialog-description"}
      >
        <DialogTitle id={"alert-dialog-title"}>
          {"Delete Template?"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} alignItems={"center"}>
            <Grid item xs={12}>
              <DialogContentText id={"alert-dialog-description"}>
                {`Are you sure you want to delete the template "${template.title}"? This action cannot be undone.`}
              </DialogContentText>
            </Grid>
            <Grid item xs={12}>
              <TextField
                type={"text"}
                InputLabelProps={{ shrink: true }}
                InputProps={{ spellCheck: "false" }}
                label={"Reason for change"}
                autoComplete={"off"}
                variant={"filled"}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                fullWidth
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} autoFocus>
            {"Cancel"}
          </Button>
          <Button onClick={handleConfirmDelete} isDisabled={reason?.length < 4}>
            {"Delete"}
          </Button>
        </DialogActions>
      </Dialog>
      <Paper variant={"outlined"} sx={{ padding: "0.5rem 0.625rem" }}>
        <Grid container spacing={2} alignItems={"center"}>
          <Grid item xs={11}>
            <Box>
              <Stack
                spacing={1}
                direction={"column"}
                justifyContent={"flex-start"}
                alignItems={"flex-start"}
              >
                <FileDetail
                  name={template.title}
                  filename={template.filename}
                  type={template.form}
                />
                {reference != null && (
                  <Box>
                    <Typography variant={"caption"} color={"text.secondary"}>
                      {`Reference: ${reference}`}
                    </Typography>
                  </Box>
                )}
                {template.attributes &&
                  Object.keys(template.attributes).length > 0 && (
                    <Box
                      sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 1 }}
                    >
                      {Object.entries(template.attributes).map(
                        ([key, value], index) => (
                          <Chip
                            key={index}
                            size={"small"}
                            label={`${key}: ${value}`}
                            variant={"outlined"}
                          />
                        )
                      )}
                    </Box>
                  )}
                {variablesString.length > 0 && (
                  <Box
                    sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 1 }}
                  >
                    <Typography variant={"caption"} color={"text.secondary"}>
                      {`Variables: ${variablesString}`}
                    </Typography>
                  </Box>
                )}
                {template.includedEndorsements != null && (
                  <Box>
                    <Typography variant={"caption"} color={"text.secondary"}>
                      {"Using a filtered list of endorsements"}
                    </Typography>
                  </Box>
                )}
                {template.allowedEmailTargets &&
                  template.allowedEmailTargets.length > 0 && (
                    <Box>
                      <Typography variant={"caption"} color={"text.secondary"}>
                        {"Email targets: " +
                          template.allowedEmailTargets
                            .map(({ name }) => name || "[Unknown]")
                            .join(", ")}
                      </Typography>
                    </Box>
                  )}
              </Stack>
            </Box>
          </Grid>
          <Grid item xs={1}>
            <Stack direction={"row"} justifyContent={"flex-end"}>
              <IconButton
                onClick={onEdit}
                icon={EditIcon}
                variant={"bright"}
                tooltip={"Edit template"}
              />
              {template.fileId &&
                (downloading ? (
                  <Box sx={{ padding: "5px 8px" }}>
                    <DefaultLoader size={24} />
                  </Box>
                ) : (
                  <IconButton
                    onClick={async () => download()}
                    icon={DownloadIcon}
                    variant={"bright"}
                    tooltip={"Download template"}
                  />
                ))}
              <IconButton
                onClick={handleDeleteClick}
                icon={DeleteIcon}
                variant={"bright"}
                tooltip={"Delete template"}
              />
              <Switch checked={template.active ?? true} />
            </Stack>
          </Grid>
        </Grid>
      </Paper>
    </>
  );
};

const updateEntry = (data, entry) => {
  if (entry.id == null) {
    return [
      ...(data ?? []),
      {
        ...entry,
        id: makeNewId(),
      },
    ];
  }
  return (data ?? []).map((e) => (e.id === entry.id ? entry : e));
};

const deleteEntry = (data, entry) => {
  return (data ?? []).filter((e) => e.id !== entry.id);
};

const DownloadAllButton = ({ templates, companyId }) => {
  const [isDownloading, setIsDownloading] = useState(false);

  const downloadAll = async () => {
    setIsDownloading(true);
    try {
      await Promise.all(
        templates.map((template) =>
          fileUtils.saveFile(template.fileId, template.filename, companyId)
        )
      );
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Button
      startIcon={<DownloadIcon />}
      isLoading={isDownloading}
      onClick={downloadAll}
    >
      {"Download All"}
    </Button>
  );
};

const Templates = () => {
  const { companyId } = useParams();

  const [filterByForm, setFilterByForm] = useState(null);

  const [
    loadTemplates,
    { data: templates, isLoading },
  ] = useLazyGetStaticDataQuery();

  const templatesList = useMemo(
    () => (Array.isArray(templates) ? templates : []),
    [templates]
  );

  const filteredTemplates = useMemo(
    () =>
      [...templatesList]
        .filter(({ form }) => filterByForm == null || filterByForm === form)
        .sort((a, b) => (a?.title || "").localeCompare(b?.title || "")),
    [templatesList, filterByForm]
  );

  const [update, { isLoading: isUpdating }] = useUpdateStaticDataMutation();
  const [activeTemplate, setActiveTemplate] = useState(null);
  const [newTemplateOpen, setNewTemplateOpen] = useState(false);
  const [reason, setReason] = useState("");
  const refresh = useCallback(() => {
    loadTemplates({ companyId, key: "file_templates" });
    setActiveTemplate(null);
  }, [loadTemplates, setActiveTemplate, companyId]);
  const pollForChanges = useCallback(async () => {
    const response = await utils.authenticatedFetch(
      config.endpoints.admin(`static-data/${companyId}/file_templates`),
      {}
    );
    return await response.json();
  }, [companyId]);
  // A save the server refused for template errors, held until the user
  // either overrides it or gives up: {reports, unvalidated, retry}.
  const [pendingOverride, setPendingOverride] = useState(null);
  const [overrideReason, setOverrideReason] = useState("");

  // Writes the template list. A TEMPLATE_VALIDATION_FAILED refusal opens the
  // validation dialog holding a retry with force set; anything else follows
  // the pre-validation behaviour of refreshing regardless of outcome.
  const writeTemplates = async ({ data, userLogMessage, force }) => {
    const result = await update({
      companyId,
      key: "file_templates",
      data,
      userLogMessage,
      ...(force && { force: true }),
    });
    const error =
      result?.error != null ? toStructuredError(result.error) : null;
    if (error?.type === "TEMPLATE_VALIDATION_FAILED") {
      const reports = error.body?.reports ?? [];
      setPendingOverride({
        reports,
        unvalidated: error.body?.unvalidated ?? [],
        retry: (reason) =>
          writeTemplates({
            data,
            userLogMessage: overrideLogMessage(countErrors(reports), reason),
            force: true,
          }),
      });
      return error;
    }
    refresh();
    return null;
  };

  const handleOverrideProceed = async () => {
    const pending = pendingOverride;
    setPendingOverride(null);
    const error = await pending.retry(overrideReason);
    if (error == null) {
      setOverrideReason("");
    }
  };

  const onSave = ({ template, userLogMessage, force }) =>
    writeTemplates({
      data: updateEntry(templatesList, template),
      userLogMessage,
      force,
    });

  const onDelete = ({ template, userLogMessage, force }) =>
    writeTemplates({
      data: deleteEntry(templatesList, template),
      userLogMessage,
      force,
    });
  useEffect(() => {
    if (companyId != null) {
      refresh();
    }
  }, [companyId, refresh]);

  return (
    <>
      <Component
        title={"Submission Templates"}
        loading={isLoading || isUpdating}
        options={
          <Stack direction={"row"} gap={1}>
            <Select
              value={filterByForm ?? "__ALL__"}
              onChange={(e) => {
                const value = e.target.value;
                setFilterByForm(value === "__ALL__" ? null : value);
              }}
              size={"small"}
            >
              <MenuItem value={"__ALL__"}>{"[All]"}</MenuItem>
              {support.templateTypes.map(({ key, label }) => (
                <MenuItem key={key} value={key}>
                  {label}
                </MenuItem>
              ))}
            </Select>
            <Button
              startIcon={<AddIcon />}
              onClick={() => setNewTemplateOpen(true)}
            >
              {"Add Template"}
            </Button>
            <DownloadAllButton
              templates={filteredTemplates}
              companyId={companyId}
            />
          </Stack>
        }
      >
        {companyId && (
          <RawConfigEditor
            data={templates}
            onSave={async (parsedJson, logMessage) => {
              const error = await writeTemplates({
                data: parsedJson,
                userLogMessage: logMessage,
              });
              if (error != null) {
                // Throwing keeps the raw JSON in the editor and surfaces the
                // refusal beside it while the validation dialog is open.
                throw new Error(error.message);
              }
            }}
            isSaving={isUpdating}
            pollForChanges={pollForChanges}
          >
            <Grid item xs={12}>
              <TextField
                type={"text"}
                InputLabelProps={{ shrink: true }}
                InputProps={{ spellCheck: "false" }}
                label={"Reason for change"}
                autoComplete={"off"}
                variant={"filled"}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                fullWidth
              />
            </Grid>
            {(filteredTemplates ?? []).map((template) => (
              <Grid item xs={12} key={template.id}>
                <DisplayTemplate
                  template={template}
                  onEdit={() => setActiveTemplate(template)}
                  onDelete={onDelete}
                  companyId={companyId}
                  templates={templatesList}
                />
              </Grid>
            ))}
            {(filteredTemplates ?? []).length === 0 && (
              <Grid item xs={12}>
                <Stack
                  sx={{ width: "100%", padding: "3rem 0" }}
                  alignItems={"center"}
                  justifyContent={"center"}
                >
                  <Typography color={"lightslategrey !important"}>
                    {"No templates configured at present"}
                  </Typography>
                </Stack>
              </Grid>
            )}
            <Grid item xs={12}>
              {activeTemplate?.id != null && (
                <TemplateEditor
                  isOpen={true}
                  onClose={() => setActiveTemplate(null)}
                  template={activeTemplate}
                  onSave={onSave}
                  companyId={companyId}
                  reason={reason}
                  setReason={setReason}
                  templates={templatesList}
                />
              )}
            </Grid>
            <Grid item xs={12}>
              <TemplateEditor
                isOpen={newTemplateOpen}
                onClose={() => setNewTemplateOpen(false)}
                template={{
                  form: "EXCEL",
                  active: true,
                  title: "New Template",
                  filename: "New Template.xlsx",
                  attributes: {},
                }}
                companyId={companyId}
                onSave={onSave}
                reason={reason}
                setReason={setReason}
                templates={templatesList}
              />
            </Grid>
          </RawConfigEditor>
        )}
      </Component>
      <BulkEndorsementUpload
        disabled={isLoading || isUpdating}
        templates={templatesList}
        onUpdate={refresh}
        companyId={companyId}
        reason={reason}
        setReason={setReason}
      />
      <TemplateValidationDialog
        open={pendingOverride != null}
        onClose={() => setPendingOverride(null)}
        reports={pendingOverride?.reports ?? []}
        unvalidated={pendingOverride?.unvalidated ?? []}
        onProceed={handleOverrideProceed}
        proceedLabel={"Save anyway"}
        requireReason
        reason={overrideReason}
        onReasonChange={setOverrideReason}
      />
    </>
  );
};

export default Templates;
